import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  NotificationMessageType,
  PaginationType,
} from '@nxsys/types/models/Common';
import { revenueActs } from '../action';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';
import {
  PsrBatchDetailType,
  PsrBatchRecordType,
  PsrRecordType,
} from '@nxsys/types/models/revenueFilings/psr';
import { Employment, GetLeaverToSubmitPSRParams, GetPayrollToSubmitPSRParams } from '../types';

export interface RevenueState {
  loading: boolean;
  psrData: {
    list: PsrRecordType[];
    pagination: PaginationType;
  };
  /** NX-6140: the filings list, one row per submission to Revenue. */
  psrBatchData: {
    loading: boolean;
    list: PsrBatchRecordType[];
    pagination: PaginationType;
  };
  psrBatchDetail: {
    loading: boolean;
    data: PsrBatchDetailType | null;
  };
  payrollToSubmitPSR: {
    loading: boolean;
    list: any[];
    pagination: PaginationType;
    queryParams: GetPayrollToSubmitPSRParams | null;
  };
  leaverToSubmitPSR: {
    loading: boolean;
    list: Employment[];
    pagination: PaginationType;
    queryParams: GetLeaverToSubmitPSRParams | null;
  };

  messageNotification: NotificationMessageType | null;
}

const initialState: RevenueState = {
  loading: false,
  psrData: {
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
  },
  psrBatchData: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
  },
  psrBatchDetail: {
    loading: false,
    data: null,
  },
  payrollToSubmitPSR: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  leaverToSubmitPSR: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  messageNotification: null,
};

const revenueSlice = createSlice({
  name: 'revenue',
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.messageNotification = null;
    },
    setMessage: (
      state,
      action: PayloadAction<{
        text: string;
        type: 'success' | 'error' | 'info';
      }>,
    ) => {
      state.messageNotification = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get PSR Records
    builder.addCase(revenueActs.getPsrBatches.pending, (state) => {
      state.psrBatchData.loading = true;
    });
    builder.addCase(revenueActs.getPsrBatches.fulfilled, (state, action) => {
      state.psrBatchData.loading = false;
      state.psrBatchData.list = action.payload.data;
      state.psrBatchData.pagination = action.payload.pagination;
    });
    builder.addCase(revenueActs.getPsrBatches.rejected, (state, action) => {
      state.psrBatchData.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'revenue.psr.getError' }),
      });
    });

    builder.addCase(revenueActs.getPsrBatchDetail.pending, (state) => {
      state.psrBatchDetail.loading = true;
    });
    builder.addCase(
      revenueActs.getPsrBatchDetail.fulfilled,
      (state, action) => {
        state.psrBatchDetail.loading = false;
        state.psrBatchDetail.data = action.payload;
      },
    );
    builder.addCase(revenueActs.getPsrBatchDetail.rejected, (state, action) => {
      state.psrBatchDetail.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'revenue.psr.getError' }),
      });
    });

    builder.addCase(revenueActs.getPsrRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.getPsrRecords.fulfilled, (state, action) => {
      state.loading = false;
      state.psrData.list = action.payload.data;
      state.psrData.pagination = action.payload.pagination;
    });
    builder.addCase(revenueActs.getPsrRecords.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'revenue.psr.getError' }),
      });
    });

    // Get PSR Records All
    builder.addCase(revenueActs.getPsrRecordsAll.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.getPsrRecordsAll.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(revenueActs.getPsrRecordsAll.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'revenue.psr.getError' }),
      });
    });

    // Get PSR Records Error
    builder.addCase(revenueActs.getPsrRecordsError.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.getPsrRecordsError.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(
      revenueActs.getPsrRecordsError.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({ id: 'revenue.psr.getError' }),
        });
      },
    );

    // Resubmit PSR Records
    builder.addCase(revenueActs.resubmitPsrRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.resubmitPsrRecords.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'revenue.psr.resubmitSuccess' }),
      });
    });
    builder.addCase(
      revenueActs.resubmitPsrRecords.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            (action.payload as any)?.message ||
            appIntl().formatMessage({ id: 'revenue.psr.resubmitError' }),
        });
      },
    );

    // Resubmit Leaver PSR Records
    builder.addCase(revenueActs.resubmitLeaverPSRRecords.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.resubmitLeaverPSRRecords.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'revenue.psr.resubmitSuccess' }),
      });
    });
    builder.addCase(
      revenueActs.resubmitLeaverPSRRecords.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            (action.payload as any)?.message ||
            appIntl().formatMessage({ id: 'revenue.psr.resubmitError' }),
        });
      },
    );

    // Get Payroll To Submit PSR
    builder.addCase(revenueActs.getPayrollToSubmitPSR.pending, (state) => {
      state.payrollToSubmitPSR.loading = true;
    });
    builder.addCase(revenueActs.getPayrollToSubmitPSR.fulfilled, (state, action) => {
      state.payrollToSubmitPSR.loading = false;
      state.payrollToSubmitPSR.list = action.payload.data;
      state.payrollToSubmitPSR.pagination = action.payload.pagination;
      state.payrollToSubmitPSR.queryParams = action.payload.queryParams;
    });
    builder.addCase(revenueActs.getPayrollToSubmitPSR.rejected, (state, action) => {
      state.payrollToSubmitPSR.loading = false;
      showToast({
        type: 'error',
        message: (action.payload as any)?.response?.data?.message || (action.payload as any)?.message || appIntl().formatMessage({ id: 'psr.submit.getError' }),
      });
    });

    // Get Leaver To Submit PSR
    builder.addCase(revenueActs.getLeaverToSubmitPSR.pending, (state) => {
      state.leaverToSubmitPSR.loading = true;
    });
    builder.addCase(revenueActs.getLeaverToSubmitPSR.fulfilled, (state, action) => {
      state.leaverToSubmitPSR.loading = false;
      state.leaverToSubmitPSR.list = action.payload.data;
      state.leaverToSubmitPSR.pagination = action.payload.pagination;
      state.leaverToSubmitPSR.queryParams = action.payload.queryParams;
    });
    builder.addCase(revenueActs.getLeaverToSubmitPSR.rejected, (state, action) => {
      state.leaverToSubmitPSR.loading = false;
      showToast({
        type: 'error',
        message: (action.payload as any)?.response?.data?.message || (action.payload as any)?.message || appIntl().formatMessage({ id: 'psr.leaver.getError' }),
      });
    });

    // Submit Leaver PSR
    builder.addCase(revenueActs.submitLeaverPSR.pending, () => { });
    builder.addCase(revenueActs.submitLeaverPSR.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'psr.leaver.submitSuccess' }),
      });
    });
    builder.addCase(revenueActs.submitLeaverPSR.rejected, (state, action) => {
      console.info('state', state);
      showToast({
        type: 'error',
        message: (action.payload as any)?.response?.data?.message || (action.payload as any)?.message || appIntl().formatMessage({ id: 'psr.leaver.submitError' }),
      });
    });

    // Get Payroll By ID
    builder.addCase(revenueActs.getPayrollById.rejected, (state, action) => {
      console.info('state', state);
      showToast({
        type: 'error',
        message: (action.payload as any)?.response?.data?.message || (action.payload as any)?.message || appIntl().formatMessage({ id: 'psr.submit.payroll.getError' }),
      });
    });

    // Submit PSR By Payroll IDs
    builder.addCase(revenueActs.submitPSRByPayrollIds.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.submitPSRByPayrollIds.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'psr.submit.submitSuccess' }),
      });
    });
    builder.addCase(revenueActs.submitPSRByPayrollIds.rejected, (state, action) => {
      state.loading = false;
      showToast({
        type: 'error',
        message: (action.payload as any)?.response?.data?.message || (action.payload as any)?.message || appIntl().formatMessage({ id: 'psr.submit.submitError' }),
      });
    });

    // Resubmit Leaver PSR By ID
    builder.addCase(revenueActs.resubmitLeaverPsrById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(revenueActs.resubmitLeaverPsrById.fulfilled, (state) => {
      state.loading = false;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'revenue.psr.resubmitSuccess' }),
      });
    });
    builder.addCase(
      revenueActs.resubmitLeaverPsrById.rejected,
      (state, action) => {
        state.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            (action.payload as any)?.message ||
            appIntl().formatMessage({ id: 'revenue.psr.resubmitError' }),
        });
      },
    );
  },
});

export const revenueReducer = revenueSlice.reducer;
export const revenueActions = {
  ...revenueSlice.actions,
  ...revenueActs,
};
